import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';
import MediaPicker from '../MediaPicker';

const API = 'https://api.schedpilot.com/wp-json';

// Must mirror the server font keys in jobs/renderVideoOverlays.js
const FONTS = [
  { key: 'inter',      label: 'Inter',        css: "'Inter', Arial, Helvetica, sans-serif" },
  { key: 'montserrat', label: 'Montserrat',   css: "'Montserrat', Arial, sans-serif" },
  { key: 'poppins',    label: 'Poppins',      css: "'Poppins', Arial, sans-serif" },
  { key: 'roboto',     label: 'Roboto',       css: "'Roboto', Arial, sans-serif" },
  { key: 'oswald',     label: 'Oswald',       css: "'Oswald', 'Arial Narrow', Arial, sans-serif" },
  { key: 'anton',      label: 'Anton',        css: "'Anton', Impact, sans-serif" },
  { key: 'bebas',      label: 'Bebas Neue',   css: "'Bebas Neue', Impact, sans-serif" },
  { key: 'georgia',    label: 'Georgia',      css: "Georgia, 'Times New Roman', serif" },
  { key: 'courier',    label: 'Courier',      css: "'Courier New', Courier, monospace" },
];
const fontCss = (key) => (FONTS.find(f => f.key === key) || FONTS[0]).css;

let _oid = 1;
const newOverlay = () => ({
  id: _oid++,
  type: 'text',
  text: 'Your text',
  font: 'inter',
  fontSize: 0.07,
  color: '#ffffff',
  bgColor: '#000000',
  bgEnabled: true,
  bgOpacity: 0.55,
  radius: 12,
  padding: 0.018,
  outline: false,
  outlineColor: '#000000',
  outlineWidth: 0.06, // fraction of font size
  wrap: true,
  wrapWidth: 0.92,    // max line width as a fraction of the video width (matches the no-wrap cap)
  x: 0.5,
  y: 0.85,
  align: 'center',
  fullDuration: true,
  start: 0,
  end: 3,
});
const newImageOverlay = (item) => ({
  id: _oid++,
  type: 'image',
  source_image_id: item.id,
  file_url: item.file_url,
  width: 0.3,
  opacity: 1,
  x: 0.5,
  y: 0.5,
  fullDuration: true,
  start: 0,
  end: 3,
});

const GFONTS_URL = 'https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=Inter:wght@400;700&family=Montserrat:wght@600;800&family=Oswald:wght@500;700&family=Poppins:wght@600;700&family=Roboto:wght@500;700&display=swap';

// Loader/processing animations (injected once via a <style> in the component).
const SPIN_CSS = `
@keyframes sp-spin { to { transform: rotate(360deg); } }
@keyframes sp-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.14); opacity: .7; } }
@keyframes sp-indet { 0% { transform: translateX(-130%); } 100% { transform: translateX(360%); } }
@keyframes sp-dots { 0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; } 100% { content: ''; } }
@keyframes sp-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
.sp-spin { animation: sp-spin .9s linear infinite; }
.sp-pulse { animation: sp-pulse 1.6s ease-in-out infinite; }
.sp-indet { animation: sp-indet 1.3s ease-in-out infinite; }
.sp-dots::after { content: ''; animation: sp-dots 1.4s steps(1, end) infinite; }
.sp-bob { display: inline-flex; animation: sp-bob 1.5s ease-in-out infinite; }
`;

function VideoEditor() {
  const navigate = useNavigate();

  // Load the editor fonts (match the server-rendered output) once.
  useEffect(() => {
    if (document.getElementById('sp-video-fonts')) return;
    const link = document.createElement('link');
    link.id = 'sp-video-fonts';
    link.rel = 'stylesheet';
    link.href = GFONTS_URL;
    document.head.appendChild(link);
  }, []);

  const [mode, setMode]         = useState('projects'); // projects | pick | converting | edit
  const [source, setSource]     = useState(null);        // { id, file_url }

  // saved projects
  const [projects, setProjects]       = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectId, setProjectId]     = useState(null);
  const projectIdRef = useRef(null);
  const creatingRef  = useRef(false);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  const [overlays, setOverlays] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle]       = useState('');

  // edits beyond text: trim window, image stickers, replacement audio
  const [trim, setTrim]         = useState({ start: 0, end: null }); // end null = clip end
  const [audio, setAudio]       = useState(null);                    // { id, file_url, title, volume }
  const [speed, setSpeed]       = useState(1);                       // playback multiplier
  const [showSticker, setShowSticker] = useState(false);
  const [showAudio, setShowAudio]     = useState(false);

  // pick state
  const [tab, setTab]           = useState('upload');    // upload | library
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [library, setLibrary]   = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [pickError, setPickError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [convertPct, setConvertPct] = useState(0); // MOV->MP4 conversion %

  // multi-video: `source`/`overlays`/`trim`/`audio`/`speed` are the ACTIVE video's
  // working state; `sources` is the full list and `editsRef` holds each video's
  // edits keyed by video id. Single-video behaviour is unchanged (sources length 1).
  const [sources, setSources]         = useState([]);   // [{ id, file_url, title }]
  const [activeIndex, setActiveIndex] = useState(0);
  const editsRef = useRef({});                            // { [id]: { overlays, trim, audio, speed } }
  const [picked, setPicked]           = useState([]);    // staged selection in the pick screen
  const [pickBusy, setPickBusy]       = useState(false); // an upload/convert is running

  // export state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  // Batch export: one render job per source video.
  const [exportJobs, setExportJobs] = useState([]);      // [{ sourceId, title, status, video, jobId, error }]
  const [results, setResults]       = useState([]);      // done videos: [{ id, file_url, title }]
  const exportJobsRef = useRef([]);

  // scheduling (after export)
  const [showSchedule, setShowSchedule]       = useState(false);
  const [accounts, setAccounts]               = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]); // ["{id}_{type}"]
  const [postText, setPostText]               = useState('');
  const [scheduleStart, setScheduleStart]     = useState('');   // "YYYY-MM-DDTHH:MM"
  const [spacingHours, setSpacingHours]       = useState(24);
  const [scheduling, setScheduling]           = useState(false);
  const [scheduleError, setScheduleError]     = useState('');
  const [scheduleDone, setScheduleDone]       = useState(null); // { ok, fail }
  const [accountSearch, setAccountSearch]     = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState(''); // '' = all platforms

  // preview measurement
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const [previewH, setPreviewH] = useState(0);
  const [previewW, setPreviewW] = useState(0);
  const [videoDims, setVideoDims] = useState({ w: 9, h: 16 });
  const [duration, setDuration] = useState(0);
  const dragRef = useRef(null);

  // Pinned overlays render on EVERY video (e.g. a brand watermark) — global, not
  // per-video. `applyMsg` is a transient confirmation for "apply to all".
  const [pinned, setPinned]   = useState([]);
  const [applyMsg, setApplyMsg] = useState('');

  const isPinned = (id) => pinned.some(o => o.id === id);
  const findOverlay = (id) => overlays.find(o => o.id === id) || pinned.find(o => o.id === id) || null;
  const selected = findOverlay(selectedId);

  // ── Measure preview height (for px-accurate overlay scaling). A ResizeObserver
  //    keeps it correct even when the stage resizes after the video's real
  //    aspect ratio loads — otherwise the editor text scale drifts from the render.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => { setPreviewH(el.clientHeight); setPreviewW(el.clientWidth); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode, source, videoDims]);


  // Upload one file, wait for conversion, then add it to the picked tray.
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    setPickError('');
    setPickBusy(true);
    try {
      const form = new FormData();
      form.append('video', file);
      const res = await axios.post(`${API}/custom/v1/upload-video`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      const data = res.data || {};
      if (data.video_id) {
        const t = (file.name || 'video').replace(/\.[^.]+$/, '');
        await waitProcessed(data.video_id, t);
      } else {
        setPickError(data.message || 'Upload failed.');
      }
    } catch (err) {
      setPickError(err?.response?.data?.message || 'Upload failed. The file may be too large.');
    } finally {
      setUploading(false);
      setPickBusy(false);
    }
  };

  // Upload several files in sequence (multi-select / multi-drop).
  const handleUploadFiles = async (files) => {
    const list = Array.from(files || []).filter(f => f.type.startsWith('video/'));
    if (!list.length) { setPickError('Please choose video files.'); return; }
    for (const f of list) { await handleUpload(f); }
  };

  const onDropVideo = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    handleUploadFiles(e.dataTransfer.files);
  };

  const loadLibrary = useCallback(async (silent = false) => {
    if (!silent) setLibLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getMediaLibrary?type=videos&page=1`, { credentials: 'include' }).then(x => x.json());
      if (r.code === 200 && Array.isArray(r.data)) setLibrary(r.data);
      else if (!silent) setLibrary([]);
    } catch { if (!silent) setLibrary([]); }
    if (!silent) setLibLoading(false);
  }, []);

  useEffect(() => { if (tab === 'library' && mode === 'pick') loadLibrary(); }, [tab, mode, loadLibrary]);

  // Poll the library while any video is still converting, so it flips to
  // selectable on its own (silent refresh — no full-grid spinner).
  useEffect(() => {
    if (tab !== 'library' || mode !== 'pick') return;
    const converting = library.some(v => v.status && v.status !== 'processed' && v.status !== 'failed');
    if (!converting) return;
    const t = setTimeout(() => loadLibrary(true), 3500);
    return () => clearTimeout(t);
  }, [tab, mode, library, loadLibrary]);

  // ── Audio (music) picker ──
  const [audioTab, setAudioTab]         = useState('upload');   // upload | library
  const [audioLib, setAudioLib]         = useState([]);
  const [audioLibLoading, setAudioLibLoading] = useState(false);
  const [audioUploading, setAudioUploading]   = useState(false);
  const [audioError, setAudioError]     = useState('');

  const handleAudioUpload = async (file) => {
    if (!file) return;
    setAudioUploading(true); setAudioError('');
    try {
      const form = new FormData();
      form.append('audio', file);
      const res = await axios.post(`${API}/custom/v1/upload-audio`, form, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      if (d.audio_id) { setAudio({ id: d.audio_id, file_url: d.file_url, title: d.title, volume: 1 }); setShowAudio(false); }
      else setAudioError(d.message || 'Upload failed.');
    } catch (err) {
      setAudioError(err?.response?.data?.message || 'Upload failed. Please use an MP3.');
    } finally { setAudioUploading(false); }
  };

  const loadAudioLibrary = useCallback(async () => {
    setAudioLibLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getAudioLibrary?page=1`, { credentials: 'include' }).then(x => x.json());
      setAudioLib(r.code === 200 && Array.isArray(r.data) ? r.data : []);
    } catch { setAudioLib([]); }
    setAudioLibLoading(false);
  }, []);

  useEffect(() => { if (showAudio && audioTab === 'library') loadAudioLibrary(); }, [showAudio, audioTab, loadAudioLibrary]);

  // ── Overlay editing ──
  const addOverlay = () => {
    const o = newOverlay();
    setOverlays(prev => [...prev, o]);
    setSelectedId(o.id);
  };
  const addSticker = (item) => {
    const o = newImageOverlay(item);
    setOverlays(prev => [...prev, o]);
    setSelectedId(o.id);
    setShowSticker(false);
  };
  // Patch/remove route to whichever set (per-video `overlays` or global `pinned`)
  // holds the id.
  const patchOverlay = (id, patch) => {
    if (overlays.some(o => o.id === id)) setOverlays(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));
    else if (pinned.some(o => o.id === id)) setPinned(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));
  };
  const removeOverlay = (id) => {
    setOverlays(prev => prev.filter(o => o.id !== id));
    setPinned(prev => prev.filter(o => o.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
  };

  // Move an overlay between this video's set and the global pinned set.
  const setPinnedFlag = (id, pin) => {
    const o = findOverlay(id);
    if (!o) return;
    if (pin) {
      setOverlays(prev => prev.filter(x => x.id !== id));
      setPinned(prev => (prev.some(x => x.id === id) ? prev : [...prev, o]));
    } else {
      setPinned(prev => prev.filter(x => x.id !== id));
      setOverlays(prev => (prev.some(x => x.id === id) ? prev : [...prev, o]));
    }
  };

  // Duplicate an overlay (text or image) with all its styling, nudged so it's
  // visible on top of the original. Powers the "Duplicate" button + copy/paste.
  const duplicateOverlay = (o) => {
    if (!o) return;
    const clone = { ...o, id: _oid++, x: Math.min(0.95, (o.x ?? 0.5) + 0.04), y: Math.min(0.95, (o.y ?? 0.5) + 0.05) };
    if (pinned.some(p => p.id === o.id)) setPinned(prev => [...prev, clone]);
    else setOverlays(prev => [...prev, clone]);
    setSelectedId(clone.id);
  };

  // Copy the active video's captions to every other video (one-time propagation).
  const applyCaptionsToAll = () => {
    if (sources.length < 2) return;
    const cur = sources[activeIndex];
    if (cur) editsRef.current[cur.id] = { overlays, trim, audio, speed };
    sources.forEach(sv => {
      if (!cur || sv.id === cur.id) return;
      const cloned = overlays.map(o => ({ ...o, id: _oid++ }));
      const e = editsRef.current[sv.id] || blankEdit();
      editsRef.current[sv.id] = { ...e, overlays: cloned };
    });
    saveProject({ name: title || 'Untitled project', source_video_id: (sources[0] && sources[0].id) || (cur && cur.id), state: collectState() });
    setApplyMsg(`Captions applied to all ${sources.length} videos`);
    setTimeout(() => setApplyMsg(''), 2800);
  };

  // Copy/paste a styled overlay via Cmd/Ctrl+C / +V, and duplicate via +D.
  const clipboardRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (mode !== 'edit') return;
      const tag = (document.activeElement && document.activeElement.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return; // don't hijack field edits
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      const sel = findOverlay(selectedId);
      if (k === 'c' && sel) { clipboardRef.current = sel; e.preventDefault(); }
      else if (k === 'v' && clipboardRef.current) { duplicateOverlay(clipboardRef.current); e.preventDefault(); }
      else if (k === 'd' && sel) { duplicateOverlay(sel); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedId, overlays, pinned]);

  // ── Dragging overlays on the stage ──
  const onDragStart = (e, id) => {
    e.stopPropagation();
    setSelectedId(id);
    dragRef.current = id;
  };
  const onStageMove = (e) => {
    if (!dragRef.current || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    patchOverlay(dragRef.current, { x, y });
  };
  const onStageUp = () => { dragRef.current = null; };

  // An export is meaningful if there's any edit: overlay, pinned, trim, music, speed.
  const trimActive = (Number(trim.start) || 0) > 0 || (trim.end !== null && trim.end !== undefined);
  const hasEdits = overlays.length > 0 || pinned.length > 0 || trimActive || !!audio || speed !== 1;

  // Trimmed source length, and the estimated output length after speed-up.
  const baseDuration = Math.max(0, (trim.end ?? duration) - (Number(trim.start) || 0));
  const estOutDuration = speed > 0 ? baseDuration / speed : baseDuration;

  // Does a per-video edit object have anything worth rendering? (Pinned overlays
  // count for every video.)
  const editRenderable = (e) => {
    const tActive = (Number(e.trim?.start) || 0) > 0 || (e.trim?.end !== null && e.trim?.end !== undefined);
    return (e.overlays?.length || 0) > 0 || pinned.length > 0 || tActive || !!e.audio || (typeof e.speed === 'number' && e.speed !== 1);
  };

  // ── Export: render EVERY source video (per-video overlays + pinned). One render
  //    job per video; poll them all and collect the results. ──
  const doExport = async () => {
    if (!source) return;
    // Snapshot every video's edits; the active video uses the live working state.
    const editsMap = {};
    sources.forEach(sv => { editsMap[sv.id] = editsRef.current[sv.id] || blankEdit(); });
    const cur = sources[activeIndex];
    if (cur) editsMap[cur.id] = { overlays, trim, audio, speed };

    const toExport = sources.filter(sv => editRenderable(editsMap[sv.id]));
    if (!toExport.length) { setExportError('Add a caption, trim, music or speed change to at least one video before exporting.'); return; }

    setExportError('');
    setResults([]);
    setExporting(true);
    const jobs = toExport.map((sv, i) => ({
      sourceId: sv.id,
      title: toExport.length > 1 ? `${title || 'Video'} ${i + 1}` : (title || 'Edited video'),
      status: 'queued', video: null, jobId: null, error: null,
    }));
    exportJobsRef.current = jobs;
    setExportJobs(jobs);

    for (let i = 0; i < toExport.length; i++) {
      const sv = toExport[i];
      const e = editsMap[sv.id];
      const tActive = (Number(e.trim?.start) || 0) > 0 || (e.trim?.end !== null && e.trim?.end !== undefined);
      const payload = {
        source_video_id: sv.id,
        output_title: jobs[i].title,
        trim: tActive ? { start: Number(e.trim.start) || 0, end: e.trim.end !== null ? Number(e.trim.end) : null } : null,
        audio: e.audio ? { source_audio_id: e.audio.id, volume: e.audio.volume ?? 1, match_speed: e.audio.matchSpeed !== false } : null,
        speed: typeof e.speed === 'number' ? e.speed : 1,
        overlays: [...(e.overlays || []), ...pinned].map(serializeOverlay),
      };
      try {
        const r = await fetch(`${API}/api/v1/render-video`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then(x => x.json());
        if (r.code === 200 && r.job_id) { updateExportJob(sv.id, { status: 'rendering', jobId: r.job_id }); pollOne(sv.id, r.job_id); }
        else updateExportJob(sv.id, { status: 'failed', error: r.message || 'Could not start render.' });
      } catch (err) {
        updateExportJob(sv.id, { status: 'failed', error: err.message || 'Export failed.' });
      }
    }
  };

  const updateExportJob = (sourceId, patch) => {
    const next = exportJobsRef.current.map(j => (j.sourceId === sourceId ? { ...j, ...patch } : j));
    exportJobsRef.current = next;
    setExportJobs(next);
    if (next.length && next.every(j => j.status === 'done' || j.status === 'failed')) {
      setExporting(false);
      setResults(next.filter(j => j.status === 'done' && j.video).map(j => j.video));
    }
  };

  const pollOne = (sourceId, jobId) => {
    // The node worker renders ONE job at a time, so in a big batch most jobs sit
    // 'queued' for a long while. Time spent waiting in the queue must not count
    // toward the timeout — otherwise late jobs get marked failed while the
    // backend is still happily working through the line.
    let processingTicks = 0; // polls while the job is actively rendering
    let totalTicks = 0;      // safety cap on the whole wait, queue included
    const tick = async () => {
      totalTicks++;
      try {
        const r = await fetch(`${API}/api/v1/render-status?job_id=${jobId}`, { credentials: 'include' }).then(x => x.json());
        if (r.code === 200 && r.status === 'done' && r.video) { updateExportJob(sourceId, { status: 'done', video: r.video }); return; }
        if (r.code === 200 && r.status === 'failed') { updateExportJob(sourceId, { status: 'failed', error: r.error || 'Render failed.' }); return; }
        if (r.code === 200 && r.status === 'processing') {
          processingTicks++;
          updateExportJob(sourceId, { status: 'rendering', stage: r.stage || null });
        } else if (r.code === 200 && r.status === 'queued') {
          updateExportJob(sourceId, { status: 'queued' });
        }
      } catch { /* keep polling */ }
      // 172 × 7s ≈ 20 min of ACTIVE rendering per job; ~2h total including queue time.
      if (processingTicks < 172 && totalTicks < 1030) setTimeout(tick, 7000);
      else updateExportJob(sourceId, { status: 'failed', error: 'Render timed out.' });
    };
    tick();
  };

  // ── Scheduling (after export) ──
  const SPACING_PRESETS = [
    { label: '1 hour', hours: 1 }, { label: '6 hours', hours: 6 }, { label: '12 hours', hours: 12 },
    { label: '1 day', hours: 24 }, { label: '3 days', hours: 72 }, { label: '5 days', hours: 120 }, { label: '7 days', hours: 168 },
  ];
  const pad2 = (n) => String(n).padStart(2, '0');
  const toInputValue = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const toApiDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
  const scheduleDateFor = (i) => {
    const base = scheduleStart ? new Date(scheduleStart) : new Date();
    return new Date(base.getTime() + (results.length > 1 ? i * spacingHours * 3600 * 1000 : 0));
  };

  const openScheduler = () => {
    // default start = next hour, today
    const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0);
    setScheduleStart(toInputValue(d));
    setPostText(''); setSelectedAccounts([]); setScheduleError(''); setScheduleDone(null);
    setAccountSearch(''); setAccountTypeFilter('');
    setShowSchedule(true);
    loadAccounts();
  };

  // Account platform filter + name search.
  const accountTypes = [...new Set(accounts.map(a => a.type))];
  const filteredAccounts = accounts.filter(a => {
    if (accountTypeFilter && a.type !== accountTypeFilter) return false;
    if (!accountSearch) return true;
    const q = accountSearch.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.type || '').toLowerCase().includes(q);
  });

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getAccounts`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).then(x => x.json());
      setAccounts(r.code === 200 && Array.isArray(r.accounts) ? r.accounts : []);
    } catch { setAccounts([]); }
    setAccountsLoading(false);
  };

  const toggleAccount = (acc) => {
    const key = `${acc.id}_${acc.type}`;
    setSelectedAccounts(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const doSchedule = async () => {
    if (!selectedAccounts.length) { setScheduleError('Select at least one account.'); return; }
    if (!results.length) return;
    setScheduling(true); setScheduleError('');
    const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
    let ok = 0, fail = 0;
    for (let i = 0; i < results.length; i++) {
      const v = results[i];
      const when = scheduleDateFor(i);
      try {
        const r = await fetch(`${API}/api/v1/schedulePost`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduledDate: toApiDate(when),
            text: postText,
            currentScheduledID: 0,
            postStatus: 'publish',
            timezone: tz,
            videoTitle: (v.title || postText || 'Video').slice(0, 100),
            images: [],
            videos: [{ id: v.id, file_url: v.file_url, title: v.title }],
            accounts: selectedAccounts,
            replies: [],
          }),
        }).then(x => x.json());
        if (r.code === 200) ok++; else fail++;
      } catch { fail++; }
    }
    setScheduling(false);
    setScheduleDone({ ok, fail });
  };

  const clearEditState = () => {
    setSource(null); setOverlays([]); setSelectedId(null);
    setResults([]); setExportJobs([]); setExportError(''); setTitle(''); setTab('upload');
    setTrim({ start: 0, end: null }); setAudio(null); setSpeed(1); setConvertPct(0);
    setSources([]); setActiveIndex(0); editsRef.current = {}; setPicked([]);
    setPinned([]); setApplyMsg('');
  };

  // The default per-video edit object.
  const blankEdit = () => ({ overlays: [], trim: { start: 0, end: null }, audio: null, speed: 1 });

  // Full project state for saving: the active video's working state merged into
  // the per-video map, pruned to the current sources (no orphaned entries).
  const collectState = () => {
    const perVideo = {};
    sources.forEach(sv => { perVideo[sv.id] = editsRef.current[sv.id] || blankEdit(); });
    const cur = sources[activeIndex];
    if (cur) perVideo[cur.id] = { overlays, trim, audio, speed };
    return { sources, activeIndex, perVideo, pinned, title };
  };

  // Start a brand-new project: blank slate, then pick video(s).
  const newProject = () => {
    projectIdRef.current = null; setProjectId(null);
    clearEditState(); setPickError(''); setMode('pick');
  };

  // Add/replace the videos in the CURRENT project, keeping existing edits.
  const changeVideo = () => { setPickError(''); setPicked(sources); setMode('pick'); };

  // ── Projects: list / open / save / rename / delete ──
  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getVideoProjects`, { credentials: 'include' }).then(x => x.json());
      setProjects(r.code === 200 && Array.isArray(r.data) ? r.data : []);
    } catch { setProjects([]); }
    setProjectsLoading(false);
  }, []);

  useEffect(() => { if (mode === 'projects') loadProjects(); }, [mode, loadProjects]);

  const openProject = async (id) => {
    try {
      const r = await fetch(`${API}/api/v1/getVideoProject?id=${id}`, { credentials: 'include' }).then(x => x.json());
      if (r.code !== 200 || !r.state) { return; }
      const s = r.state;
      clearEditState();
      projectIdRef.current = r.id; setProjectId(r.id);
      setTitle(s.title || r.name || '');

      // Multi shape: { sources, activeIndex, perVideo }. Migrate the old single
      // shape { source, overlays, trim, audio, speed } to a 1-entry array.
      let srcs = Array.isArray(s.sources) ? s.sources : (s.source && s.source.id ? [s.source] : []);
      let perVideo = (s.perVideo && typeof s.perVideo === 'object') ? s.perVideo : {};
      if (!s.perVideo && s.source && s.source.id) {
        perVideo = { [s.source.id]: { overlays: s.overlays || [], trim: s.trim || { start: 0, end: null }, audio: s.audio || null, speed: typeof s.speed === 'number' ? s.speed : 1 } };
      }
      const pinnedOvs = Array.isArray(s.pinned) ? s.pinned : [];
      // Keep local overlay ids unique against the module counter.
      [...Object.values(perVideo).flatMap(e => e.overlays || []), ...pinnedOvs].forEach(o => { if (typeof o.id === 'number' && o.id >= _oid) _oid = o.id + 1; });
      setPinned(pinnedOvs);

      editsRef.current = perVideo;
      setSources(srcs);
      const idx = Math.min(s.activeIndex || 0, Math.max(0, srcs.length - 1));
      setActiveIndex(idx);
      if (srcs.length && srcs[idx]) {
        const e = perVideo[srcs[idx].id] || blankEdit();
        setOverlays(e.overlays || []);
        setTrim(e.trim || { start: 0, end: null });
        setAudio(e.audio || null);
        setSpeed(typeof e.speed === 'number' ? e.speed : 1);
        setSource(srcs[idx]); setConvertPct(100); setMode('edit');
      } else {
        setMode('pick');
      }
    } catch { /* ignore */ }
  };

  const saveProject = useCallback(async (patch) => {
    try {
      const body = { ...patch };
      if (projectIdRef.current) body.id = projectIdRef.current;
      const r = await fetch(`${API}/api/v1/saveVideoProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (r.code === 200 && r.id && !projectIdRef.current) { projectIdRef.current = r.id; setProjectId(r.id); }
      return r.id;
    } catch { return null; }
  }, []);

  const renameProject = async (id, name) => {
    setProjects(ps => ps.map(p => (p.id === id ? { ...p, name } : p)));
    await saveProjectName(id, name);
  };
  const saveProjectName = async (id, name) => {
    try {
      await fetch(`${API}/api/v1/saveVideoProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name }),
      });
    } catch { /* ignore */ }
  };
  const deleteProject = async (id) => {
    setProjects(ps => ps.filter(p => p.id !== id));
    try {
      await fetch(`${API}/api/v1/deleteVideoProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch { /* ignore */ }
  };

  // Create the project the first time we enter edit on a fresh (unsaved) clip.
  useEffect(() => {
    if (mode !== 'edit' || !source || projectIdRef.current || creatingRef.current) return;
    creatingRef.current = true;
    saveProject({
      name: title || 'Untitled project',
      source_video_id: (sources[0] && sources[0].id) || source.id,
      state: collectState(),
    }).finally(() => { creatingRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, source]);

  // Autosave edits (debounced) once the project exists.
  useEffect(() => {
    if (mode !== 'edit' || !source || !projectId) return;
    const t = setTimeout(() => {
      saveProject({
        name: title || 'Untitled project',
        source_video_id: (sources[0] && sources[0].id) || source.id,
        state: collectState(),
      });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, source, title, overlays, trim, audio, speed, projectId, sources, activeIndex, pinned]);

  // ── Multi-video: switch active clip, stage a selection, start editing ──
  const switchTo = (i) => {
    if (i === activeIndex || i < 0 || i >= sources.length) return;
    const cur = sources[activeIndex];
    if (cur) editsRef.current[cur.id] = { overlays, trim, audio, speed }; // persist current
    const next = sources[i];
    const e = editsRef.current[next.id] || blankEdit();
    setOverlays(e.overlays || []); setSelectedId(null);
    setTrim(e.trim || { start: 0, end: null }); setAudio(e.audio || null);
    setSpeed(typeof e.speed === 'number' ? e.speed : 1);
    setSource(next); setActiveIndex(i); setConvertPct(100); setResults([]); setExportJobs([]); setExportError('');
  };

  const togglePick = (v) => {
    setPicked(prev => prev.find(p => p.id === v.id)
      ? prev.filter(p => p.id !== v.id)
      : [...prev, { id: v.id, file_url: v.file_url, title: v.title }]);
  };
  const removePick = (id) => setPicked(prev => prev.filter(p => p.id !== id));

  const startEditing = () => {
    if (!picked.length) return;
    const map = { ...editsRef.current };
    picked.forEach(p => { if (!map[p.id]) map[p.id] = blankEdit(); });
    editsRef.current = map;
    setSources(picked);
    setActiveIndex(0);
    const first = picked[0];
    const e = map[first.id];
    setOverlays(e.overlays || []); setSelectedId(null);
    setTrim(e.trim || { start: 0, end: null }); setAudio(e.audio || null); setSpeed(typeof e.speed === 'number' ? e.speed : 1);
    setSource(first); setConvertPct(100);
    setTitle(prev => prev || first.title || 'Untitled project');
    setMode('edit');
  };

  // Poll an uploaded video's MOV->MP4 conversion, then add it to the picked tray.
  const waitProcessed = (videoId, fallbackTitle) => new Promise((resolve) => {
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const r = await fetch(`${API}/api/v1/video-status?id=${videoId}`, { credentials: 'include' }).then(x => x.json());
        if (r.code === 200 && typeof r.progress === 'number') setConvertPct(r.progress);
        if (r.code === 200 && r.status === 'processed') {
          setPicked(prev => prev.find(p => p.id === r.id) ? prev : [...prev, { id: r.id, file_url: r.file_url, title: r.title || fallbackTitle }]);
          resolve(); return;
        }
        if (r.code === 200 && r.status === 'failed') { setPickError('A video could not be processed.'); resolve(); return; }
      } catch { /* keep polling */ }
      if (tries < 240) setTimeout(tick, 2500);
      else { setPickError('Conversion timed out.'); resolve(); }
    };
    tick();
  });

  // ── Overlay preview style (px-accurate vs the rendered output) ──
  const overlayStyle = (o) => {
    const fontPx = o.fontSize * previewH;
    const padPx  = o.padding * previewH;
    const radPx  = o.radius * (previewH / (videoDims.h || 1));
    const bg = o.bgEnabled ? hexToRgba(o.bgColor, o.bgOpacity) : 'transparent';
    // Letter outline (a crisp border around the glyphs) — simulated with an
    // 8-direction text-shadow so unbackgrounded text stays readable on video.
    let textShadow = 'none';
    if (o.outline) {
      const w = Math.max(0.5, (o.outlineWidth || 0.06) * fontPx);
      const c = o.outlineColor || '#000000';
      textShadow = [
        `${-w}px ${-w}px 0 ${c}`, `${w}px ${-w}px 0 ${c}`,
        `${-w}px ${w}px 0 ${c}`,  `${w}px ${w}px 0 ${c}`,
        `${-w}px 0 0 ${c}`, `${w}px 0 0 ${c}`, `0 ${-w}px 0 ${c}`, `0 ${w}px 0 ${c}`,
      ].join(', ');
    }
    // Pin at the top-left and position purely with a pixel transform. Using
    // `left:X%` would make the browser cap an auto-width box at "stage − left",
    // so a box near the right edge wraps to one word/letter per line. With
    // left:0 the box always has the full stage width to lay out in, so the wrap
    // width depends only on max-width — not on where the box sits.
    const wrapW = o.wrap !== false ? (o.wrapWidth || 0.92) : 0.92;
    return {
      position: 'absolute',
      left: 0,
      top: 0,
      transform: `translate(${o.x * previewW}px, ${o.y * previewH}px) translate(-50%, -50%)`,
      fontFamily: fontCss(o.font),
      fontSize: `${fontPx}px`,
      fontWeight: 700,
      lineHeight: 1.28,
      color: o.color,
      background: bg,
      borderRadius: `${radPx}px`,
      padding: o.bgEnabled ? `${padPx}px ${padPx * 1.2}px` : 0,
      textShadow,
      textAlign: o.align,
      // Wrap to the next line at the chosen width, otherwise keep on one line.
      whiteSpace: o.wrap !== false ? 'pre-wrap' : 'pre',
      overflowWrap: o.wrap !== false ? 'break-word' : 'normal',
      wordBreak: o.wrap !== false ? 'break-word' : 'normal',
      cursor: 'move',
      userSelect: 'none',
      maxWidth: previewW ? `${Math.round(wrapW * previewW)}px` : '92%',
      outline: o.id === selectedId ? '2px solid #4f46e5' : '1px dashed rgba(255,255,255,0.4)',
      outlineOffset: 2,
    };
  };

  const imageOverlayStyle = (o) => ({
    position: 'absolute',
    left: `${o.x * 100}%`,
    top: `${o.y * 100}%`,
    transform: 'translate(-50%, -50%)',
    width: `${o.width * 100}%`,
    height: 'auto',
    opacity: o.opacity,
    cursor: 'move',
    userSelect: 'none',
    outline: o.id === selectedId ? '2px solid #4f46e5' : '1px dashed rgba(255,255,255,0.4)',
    outlineOffset: 2,
  });

  return (
    <MainStructure activeItem="creator-studio">
      <style>{SPIN_CSS}</style>
      <div className="container-fluid p-0">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => { if (mode === 'projects') navigate('/creator-studio'); else setMode('projects'); }}
              style={ghostBtn}
              title="Back"
            >
              <Icon.ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="h5 mb-0" style={{ fontWeight: 800, color: '#0f172a' }}>Video Editor</h1>
              <span className="small" style={{ color: '#64748b' }}>
                {mode === 'projects' ? 'Your saved projects' : 'Add styled text, then export to your library'}
              </span>
            </div>
          </div>
          {mode === 'edit' && (
            <div className="d-flex align-items-center gap-2">
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Video title"
                style={{ border: '1px solid #d1d9e0', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem', minWidth: 180 }}
              />
              <button onClick={doExport} disabled={exporting || (sources.length <= 1 && !hasEdits)} style={primaryBtn(exporting || (sources.length <= 1 && !hasEdits))}>
                <Icon.Download size={15} /> {sources.length > 1 ? `Export ${sources.length} videos` : 'Export to library'}
              </button>
            </div>
          )}
        </div>

        {/* ── PROJECTS ── */}
        {mode === 'projects' && (
          <div style={card}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 700, color: '#0f172a' }}>Your projects</span>
              <button onClick={newProject} style={primaryBtn(false)}><Icon.Plus size={15} /> New project</button>
            </div>

            {projectsLoading ? (
              <div className="text-center py-5"><Icon.Loader size={22} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
            ) : projects.length === 0 ? (
              <div className="text-center py-5" style={{ color: '#94a3b8' }}>
                <Icon.Film size={34} style={{ marginBottom: 10 }} />
                <div style={{ fontSize: '0.9rem' }}>No projects yet.</div>
                <button onClick={newProject} style={{ ...primaryBtn(false), marginTop: 14 }}><Icon.Plus size={15} /> New project</button>
              </div>
            ) : (
              <div className="row g-3">
                {projects.map(p => (
                  <div key={p.id} className="col-6 col-md-4 col-xl-3">
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                      <div onClick={() => openProject(p.id)} style={{ aspectRatio: '16/10', background: '#0f172a', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                        {p.source_url ? (
                          <video src={`${p.source_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon.Film size={26} color="#475569" />
                          </div>
                        )}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.18)' }}>
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon.Edit2 size={15} color="#0f172a" />
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <ProjectName project={p} onSave={renameProject} />
                        <div className="d-flex align-items-center justify-content-between mt-1">
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{fmtDate(p.updated_at)}</span>
                          <Icon.Trash2 size={14} style={{ color: '#ef4444', cursor: 'pointer' }} title="Delete project"
                            onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this project?')) deleteProject(p.id); }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PICK (multi-select) ── */}
        {mode === 'pick' && (
          <div style={card}>
            <div className="d-flex gap-2 mb-3">
              {['upload', 'library'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={tabBtn(tab === t)}>
                  {t === 'upload' ? 'Upload videos' : 'Choose from library'}
                </button>
              ))}
            </div>

            {pickError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{pickError}</div>}

            {tab === 'upload' ? (
              <label
                style={dragOver ? dropzoneActive : dropzone}
                onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
                onDrop={onDropVideo}
              >
                <input type="file" accept="video/*" multiple hidden disabled={uploading}
                  onChange={e => handleUploadFiles(e.target.files)} />
                {uploading ? (
                  <div className="text-center">
                    <Icon.Loader size={26} className="mb-2 sp-spin" style={{ color: '#4f46e5' }} />
                    <div style={{ fontSize: '0.9rem', color: '#334155' }}>{uploadPct < 100 ? `Uploading… ${uploadPct}%` : 'Converting…'}</div>
                    <div style={{ width: 240, height: 6, background: '#e2e8f0', borderRadius: 4, margin: '10px auto 0' }}>
                      <div style={{ width: `${uploadPct}%`, height: '100%', background: '#4f46e5', borderRadius: 4 }} />
                    </div>
                  </div>
                ) : (
                  <div className="text-center" style={{ pointerEvents: 'none' }}>
                    <Icon.UploadCloud size={34} style={{ color: dragOver ? '#4f46e5' : '#94a3b8', marginBottom: 10 }} />
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{dragOver ? 'Drop your videos here' : 'Click to upload or drag & drop videos'}</div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>You can select multiple · MP4, MOV, WebM · MOV is auto-converted</div>
                  </div>
                )}
              </label>
            ) : (
              <div>
                {libLoading ? (
                  <div className="text-center py-5"><Icon.Loader size={22} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
                ) : library.length === 0 ? (
                  <div className="text-center py-5" style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No videos in your library yet.</div>
                ) : (
                  <div className="row g-3">
                    {library.map(v => {
                      const isPicked = !!picked.find(p => p.id === v.id);
                      // A library video is selectable only once it's converted to MP4.
                      const ready = (v.status || 'processed') === 'processed';
                      const failed = v.status === 'failed';
                      return (
                        <div key={v.id} className="col-6 col-md-4 col-xl-3">
                          <button
                            onClick={() => { if (ready) togglePick(v); }}
                            disabled={!ready}
                            style={{ ...libItem, cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.85, borderColor: isPicked ? '#4f46e5' : '#e2e8f0', boxShadow: isPicked ? '0 0 0 2px #c7d2fe' : 'none' }}>
                            <div style={{ position: 'relative' }}>
                              <video src={`${v.file_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                              {/* Pick toggle — only when ready */}
                              {ready && (
                                <div style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: isPicked ? '#4f46e5' : 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {isPicked ? <Icon.Check size={14} color="#fff" /> : <Icon.Plus size={14} color="#fff" />}
                                </div>
                              )}
                              {/* Status badge */}
                              <div style={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.66rem', fontWeight: 700, color: '#fff', borderRadius: 5, padding: '2px 6px', background: failed ? 'rgba(220,38,38,0.92)' : ready ? 'rgba(22,163,74,0.92)' : 'rgba(79,70,229,0.92)' }}>
                                {ready ? <><Icon.Check size={11} /> Ready</>
                                  : failed ? <><Icon.AlertCircle size={11} /> Failed</>
                                  : <><Icon.Loader size={11} className="sp-spin" /> Converting…</>}
                              </div>
                              {/* Dim overlay while converting */}
                              {!ready && !failed && (
                                <div style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(15,23,42,0.35)' }} />
                              )}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title || 'Untitled'}</div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {library.some(v => v.status && v.status !== 'processed' && v.status !== 'failed') && (
                  <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon.Loader size={13} className="sp-spin" /> Some videos are still converting — they'll become selectable automatically.
                  </div>
                )}
              </div>
            )}

            {/* Selection tray */}
            {(picked.length > 0 || pickBusy) && (
              <div style={{ marginTop: 16, borderTop: '1px solid #eef2f7', paddingTop: 14 }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                    Selected {picked.length > 0 ? `(${picked.length})` : ''}
                  </span>
                  <button onClick={startEditing} disabled={!picked.length || pickBusy} style={primaryBtn(!picked.length || pickBusy)}>
                    {pickBusy ? 'Processing…' : `Start editing${picked.length > 1 ? ` (${picked.length} videos)` : ''}`}
                  </button>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {picked.map((p, i) => (
                    <div key={p.id} style={{ position: 'relative', width: 84 }}>
                      <video src={`${p.file_url}#t=0.1`} preload="metadata" style={{ width: 84, height: 112, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                      <span style={{ position: 'absolute', bottom: 4, left: 4, fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: 'rgba(15,23,42,0.7)', borderRadius: 4, padding: '0 5px' }}>{i + 1}</span>
                      <button onClick={() => removePick(p.id)} title="Remove"
                        style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <Icon.X size={12} />
                      </button>
                    </div>
                  ))}
                  {pickBusy && (
                    <div style={{ width: 84, height: 112, borderRadius: 8, border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon.Loader size={18} className="sp-spin" style={{ color: '#94a3b8' }} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CONVERTING ── */}
        {mode === 'converting' && (
          <div style={{ ...card, textAlign: 'center', padding: '56px 24px' }}>
            {/* spinning ring with a bobbing film icon in the middle */}
            <div style={{ position: 'relative', width: 86, height: 86, margin: '0 auto 22px' }}>
              <div className="sp-spin" style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid #e0e7ff', borderTopColor: '#4f46e5' }} />
              <div className="sp-pulse" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.12), transparent 70%)' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="sp-bob"><Icon.Film size={30} style={{ color: '#4f46e5' }} /></span>
              </div>
            </div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Converting your video<span className="sp-dots" />
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 auto', maxWidth: 380 }}>
              We're converting it to MP4 so it's ready to edit. This usually takes under a minute — you can keep this tab open.
            </p>
            {/* Real progress once ffmpeg reports it; an indeterminate sweep until then. */}
            <div style={{ width: 240, height: 6, background: '#eef2f7', borderRadius: 4, margin: '20px auto 0', overflow: 'hidden' }}>
              {convertPct > 0 ? (
                <div style={{ height: '100%', width: `${convertPct}%`, borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #a855f7)', transition: 'width .4s ease' }} />
              ) : (
                <div className="sp-indet" style={{ height: '100%', width: '40%', borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #a855f7)' }} />
              )}
            </div>
            {convertPct > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8, fontWeight: 600 }}>{convertPct}%</div>
            )}
          </div>
        )}

        {/* ── EDIT ── */}
        {mode === 'edit' && source && (
          <div className="row g-3">
            {/* Stage */}
            <div className="col-12 col-lg-8">
              {/* Video switcher strip (only when editing more than one video) */}
              {sources.length > 1 && (
                <div className="d-flex align-items-center gap-2 mb-2" style={{ overflowX: 'auto', paddingBottom: 4 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>{sources.length} videos:</span>
                  {sources.map((sv, i) => (
                    <button key={sv.id} onClick={() => switchTo(i)} title={sv.title || `Video ${i + 1}`}
                      style={{ position: 'relative', flex: '0 0 auto', width: 52, height: 70, borderRadius: 8, overflow: 'hidden', padding: 0, cursor: 'pointer', background: '#000', border: i === activeIndex ? '2px solid #4f46e5' : '1px solid #e2e8f0' }}>
                      <video src={`${sv.file_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: 'rgba(15,23,42,0.7)', borderRadius: 3, padding: '0 4px' }}>{i + 1}</span>
                    </button>
                  ))}
                </div>
              )}
              <div style={{ background: '#0f172a', borderRadius: 14, padding: 16, display: 'flex', justifyContent: 'center' }}>
                <div
                  ref={stageRef}
                  onMouseMove={onStageMove}
                  onMouseUp={onStageUp}
                  onMouseLeave={onStageUp}
                  onTouchMove={onStageMove}
                  onTouchEnd={onStageUp}
                  onMouseDown={() => setSelectedId(null)}
                  style={{
                    position: 'relative',
                    aspectRatio: `${videoDims.w} / ${videoDims.h}`,
                    maxHeight: '70vh',
                    maxWidth: '100%',
                    background: '#000',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <video
                    key={source.id}
                    ref={videoRef}
                    src={source.file_url}
                    controls
                    playsInline
                    onLoadedMetadata={(e) => {
                      const v = e.target;
                      if (v.videoWidth && v.videoHeight) setVideoDims({ w: v.videoWidth, h: v.videoHeight });
                      setDuration(v.duration || 0);
                    }}
                    style={{ width: '100%', height: '100%', display: 'block' }}
                  />
                  {[...overlays, ...pinned].map(o => (
                    o.type === 'image' ? (
                      <img
                        key={o.id}
                        src={o.file_url}
                        alt=""
                        draggable={false}
                        onMouseDown={(e) => onDragStart(e, o.id)}
                        onTouchStart={(e) => onDragStart(e, o.id)}
                        style={imageOverlayStyle(o)}
                      />
                    ) : (
                      <div
                        key={o.id}
                        onMouseDown={(e) => onDragStart(e, o.id)}
                        onTouchStart={(e) => onDragStart(e, o.id)}
                        style={overlayStyle(o)}
                      >
                        {o.text}
                      </div>
                    )
                  ))}
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                <div className="d-flex gap-2 flex-wrap">
                  <button onClick={addOverlay} style={primaryBtn(false)}><Icon.Type size={15} /> Add text</button>
                  <button onClick={() => setShowSticker(true)} style={ghostBtn}><Icon.Image size={15} /> Add image</button>
                  {sources.length > 1 && (
                    <button onClick={applyCaptionsToAll} style={ghostBtn} title="Copy this video's captions to every video">
                      <Icon.Copy size={15} /> Apply captions to all
                    </button>
                  )}
                </div>
                <button onClick={changeVideo} style={ghostBtn}>{sources.length > 1 ? 'Edit videos' : 'Change video'}</button>
              </div>
              {applyMsg && <div style={{ fontSize: '0.78rem', color: '#16a34a', marginTop: 6, fontWeight: 600 }}><Icon.Check size={13} /> {applyMsg}</div>}
              {exportError && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: '0.82rem' }}>{exportError}</div>}

              {/* Trim */}
              <div style={{ ...card, marginTop: 12 }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={panelLabel}>Trim</span>
                  {trimActive && <button style={miniBtn} onClick={() => setTrim({ start: 0, end: null })}>Reset</button>}
                </div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{ ...smallLabel, minWidth: 92, margin: 0 }}>Start {trim.start.toFixed(1)}s</span>
                  <input type="range" min={0} max={Math.max(0.1, duration)} step={0.1} value={trim.start}
                    onChange={e => { const s = Math.min(Number(e.target.value), (trim.end ?? duration) - 0.1); setTrim(t => ({ ...t, start: Math.max(0, s) })); }}
                    style={{ flex: 1 }} />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ ...smallLabel, minWidth: 92, margin: 0 }}>End {(trim.end ?? duration).toFixed(1)}s</span>
                  <input type="range" min={0} max={Math.max(0.1, duration)} step={0.1} value={trim.end ?? duration}
                    onChange={e => { const en = Math.max(Number(e.target.value), trim.start + 0.1); setTrim(t => ({ ...t, end: en >= duration ? null : en })); }}
                    style={{ flex: 1 }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>
                  {trimActive ? `Output length: ${((trim.end ?? duration) - trim.start).toFixed(1)}s` : 'Using the full clip'}
                </div>
              </div>

              {/* Speed */}
              <div style={{ ...card, marginTop: 12 }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={panelLabel}>Speed</span>
                  {speed !== 1 && <button style={miniBtn} onClick={() => setSpeed(1)}>Reset</button>}
                </div>
                <div className="d-flex gap-1 flex-wrap">
                  {[1, 1.5, 2, 3, 5].map(s => (
                    <button key={s} onClick={() => setSpeed(s)} style={segBtn(speed === s)}>
                      {s === 1 ? 'Normal' : `${s}×`}
                    </button>
                  ))}
                </div>
                {speed !== 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: '0.8rem' }}>
                    <Icon.Clock size={14} color="#64748b" />
                    <span style={{ color: '#64748b' }}>{baseDuration.toFixed(1)}s</span>
                    <Icon.ArrowRight size={13} color="#94a3b8" />
                    <span style={{ color: '#4f46e5', fontWeight: 700 }}>≈ {estOutDuration.toFixed(1)}s</span>
                    <span style={{ color: '#94a3b8' }}>at {speed}× speed</span>
                  </div>
                )}
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 6 }}>
                  Speeds up the video and its audio. Estimate is based on the {trimActive ? 'trimmed' : 'full'} clip length.
                </div>
              </div>

              {/* Music */}
              <div style={{ ...card, marginTop: 12 }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={panelLabel}>Music</span>
                </div>
                {audio ? (
                  <>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Icon.Music size={16} color="#4f46e5" />
                      <span style={{ fontSize: '0.85rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{audio.title || 'Audio track'}</span>
                      <button style={{ ...miniBtn, color: '#ef4444', marginLeft: 'auto' }} onClick={() => setAudio(null)}>Remove</button>
                    </div>
                    <audio src={audio.file_url} controls style={{ width: '100%', height: 34 }} />
                    <div className="mt-2">
                      <label style={smallLabel}>Volume {Math.round((audio.volume ?? 1) * 100)}%</label>
                      <input type="range" min={0} max={150} value={Math.round((audio.volume ?? 1) * 100)}
                        onChange={e => setAudio(a => ({ ...a, volume: Number(e.target.value) / 100 }))} style={{ width: '100%' }} />
                    </div>
                    {speed !== 1 && (
                      <label className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={audio.matchSpeed !== false} onChange={e => setAudio(a => ({ ...a, matchSpeed: e.target.checked }))} />
                        Speed up the music too ({speed}×)
                      </label>
                    )}
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                      Replaces the video's original audio on export.
                      {speed !== 1 && audio.matchSpeed === false && ' Music plays at normal speed and is trimmed to the video length.'}
                    </div>
                  </>
                ) : (
                  <button style={ghostBtn} onClick={() => { setShowAudio(true); setAudioError(''); }}><Icon.Music size={14} /> Add music</button>
                )}
              </div>
            </div>

            {/* Properties panel */}
            <div className="col-12 col-lg-4">
              <div style={card}>
                {overlays.length === 0 && pinned.length === 0 ? (
                  <div className="text-center py-4" style={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                    Click <strong>Add text</strong> or <strong>Add image</strong> to place an overlay, then drag it where you want. You can also trim the clip and add music below.
                  </div>
                ) : !selected ? (
                  <div>
                    <p style={panelLabel}>Layers · this video</p>
                    {overlays.length === 0 && <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 8 }}>No captions on this video yet.</div>}
                    {overlays.map(o => (
                      <div key={o.id} onClick={() => setSelectedId(o.id)} style={layerRow}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {o.type === 'image' ? <Icon.Image size={13} /> : <Icon.Type size={13} />}
                          {o.type === 'image' ? 'Image sticker' : (o.text || 'Text')}
                        </span>
                        <Icon.Trash2 size={14} style={{ color: '#ef4444', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); removeOverlay(o.id); }} />
                      </div>
                    ))}
                    {pinned.length > 0 && (
                      <>
                        <p style={{ ...panelLabel, marginTop: 14 }}>Pinned · all videos</p>
                        {pinned.map(o => (
                          <div key={o.id} onClick={() => setSelectedId(o.id)} style={{ ...layerRow, borderColor: '#c7d2fe', background: '#f5f6ff' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Icon.Bookmark size={13} color="#4f46e5" />
                              {o.type === 'image' ? 'Image sticker' : (o.text || 'Text')}
                            </span>
                            <Icon.Trash2 size={14} style={{ color: '#ef4444', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); removeOverlay(o.id); }} />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                ) : selected.type === 'image' ? (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p style={{ ...panelLabel, margin: 0 }}>Image settings</p>
                      <div className="d-flex gap-2">
                        <button onClick={() => setSelectedId(null)} style={miniBtn}>Layers</button>
                        <button onClick={() => duplicateOverlay(selected)} style={miniBtn} title="Duplicate (Ctrl/Cmd+D)">Duplicate</button>
                        <button onClick={() => removeOverlay(selected.id)} style={{ ...miniBtn, color: '#ef4444' }}>Delete</button>
                      </div>
                    </div>

                    <img src={selected.file_url} alt="" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', background: '#f1f5f9', borderRadius: 8, marginBottom: 10 }} />

                    {sources.length > 1 && (
                      <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={isPinned(selected.id)} onChange={e => setPinnedFlag(selected.id, e.target.checked)} />
                        <Icon.Bookmark size={13} /> Show on all videos
                      </label>
                    )}

                    <Row label={`Size ${Math.round(selected.width * 100)}%`}>
                      <input type="range" min={5} max={100} value={Math.round(selected.width * 100)}
                        onChange={e => patchOverlay(selected.id, { width: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                    </Row>
                    <Row label={`Opacity ${Math.round(selected.opacity * 100)}%`}>
                      <input type="range" min={10} max={100} value={Math.round(selected.opacity * 100)}
                        onChange={e => patchOverlay(selected.id, { opacity: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                    </Row>

                    <Row label="Timing">
                      <label className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', color: '#475569' }}>
                        <input type="checkbox" checked={selected.fullDuration} onChange={e => patchOverlay(selected.id, { fullDuration: e.target.checked })} />
                        Show for the whole video
                      </label>
                    </Row>
                    {!selected.fullDuration && (
                      <div className="d-flex gap-2 mb-1">
                        <div className="flex-fill">
                          <label style={smallLabel}>Start (s)</label>
                          <input type="number" min={0} step={0.5} value={selected.start} onChange={e => patchOverlay(selected.id, { start: e.target.value })} style={field} />
                        </div>
                        <div className="flex-fill">
                          <label style={smallLabel}>End (s){duration ? ` · max ${duration.toFixed(1)}` : ''}</label>
                          <input type="number" min={0} step={0.5} value={selected.end} onChange={e => patchOverlay(selected.id, { end: e.target.value })} style={field} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p style={{ ...panelLabel, margin: 0 }}>Text settings</p>
                      <div className="d-flex gap-2">
                        <button onClick={() => setSelectedId(null)} style={miniBtn}>Layers</button>
                        <button onClick={() => duplicateOverlay(selected)} style={miniBtn} title="Duplicate (Ctrl/Cmd+D)">Duplicate</button>
                        <button onClick={() => removeOverlay(selected.id)} style={{ ...miniBtn, color: '#ef4444' }}>Delete</button>
                      </div>
                    </div>

                    <textarea value={selected.text} onChange={e => patchOverlay(selected.id, { text: e.target.value })}
                      rows={2} style={field} placeholder="Type your text…" />

                    {sources.length > 1 && (
                      <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={isPinned(selected.id)} onChange={e => setPinnedFlag(selected.id, e.target.checked)} />
                        <Icon.Bookmark size={13} /> Show on all videos (brand/pinned)
                      </label>
                    )}

                    <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.wrap !== false} onChange={e => patchOverlay(selected.id, { wrap: e.target.checked })} />
                      Wrap long lines
                    </label>
                    {selected.wrap !== false && (
                      <Row label={`Max width ${Math.round((selected.wrapWidth || 0.92) * 100)}%`}>
                        <input type="range" min={20} max={100} value={Math.round((selected.wrapWidth || 0.92) * 100)}
                          onChange={e => patchOverlay(selected.id, { wrapWidth: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                      </Row>
                    )}

                    <Row label="Font">
                      <select value={selected.font} onChange={e => patchOverlay(selected.id, { font: e.target.value })} style={field}>
                        {FONTS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                    </Row>

                    <Row label={`Size ${Math.round(selected.fontSize * 100)}`}>
                      <input type="range" min={2} max={20} value={Math.round(selected.fontSize * 100)}
                        onChange={e => patchOverlay(selected.id, { fontSize: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                    </Row>

                    <div className="d-flex gap-3 mb-2">
                      <div className="flex-fill">
                        <label style={smallLabel}>Text color</label>
                        <input type="color" value={selected.color} onChange={e => patchOverlay(selected.id, { color: e.target.value })} style={colorInput} />
                      </div>
                      <div className="flex-fill">
                        <label style={smallLabel}>Background color</label>
                        <input type="color" value={selected.bgColor} onChange={e => patchOverlay(selected.id, { bgColor: e.target.value, bgEnabled: true })} style={colorInput} />
                      </div>
                    </div>

                    {/* Background on/off — plain text when off */}
                    <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.bgEnabled} onChange={e => patchOverlay(selected.id, { bgEnabled: e.target.checked })} />
                      Background behind text
                    </label>

                    {selected.bgEnabled && (
                      <>
                        <Row label={`Background opacity ${Math.round(selected.bgOpacity * 100)}%`}>
                          <input type="range" min={0} max={100} value={Math.round(selected.bgOpacity * 100)}
                            onChange={e => patchOverlay(selected.id, { bgOpacity: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                        </Row>
                        <Row label={`Padding ${Math.round((selected.padding || 0) * 100)}`}>
                          <input type="range" min={0} max={8} step={0.5} value={Math.round((selected.padding || 0) * 1000) / 10}
                            onChange={e => patchOverlay(selected.id, { padding: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                        </Row>
                        <Row label={`Corner radius ${selected.radius}px`}>
                          <input type="range" min={0} max={80} value={selected.radius}
                            onChange={e => patchOverlay(selected.id, { radius: Number(e.target.value) })} style={{ width: '100%' }} />
                        </Row>
                      </>
                    )}

                    {/* Letter outline — keeps plain text readable on video */}
                    <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!selected.outline} onChange={e => patchOverlay(selected.id, { outline: e.target.checked })} />
                      Outline around letters
                    </label>
                    {selected.outline && (
                      <div className="d-flex gap-3 mb-2 align-items-end">
                        <div>
                          <label style={smallLabel}>Outline color</label>
                          <input type="color" value={selected.outlineColor || '#000000'} onChange={e => patchOverlay(selected.id, { outlineColor: e.target.value })} style={{ ...colorInput, width: 54 }} />
                        </div>
                        <div className="flex-fill">
                          <label style={smallLabel}>Thickness {Math.round((selected.outlineWidth || 0.06) * 100)}</label>
                          <input type="range" min={1} max={20} value={Math.round((selected.outlineWidth || 0.06) * 100)}
                            onChange={e => patchOverlay(selected.id, { outlineWidth: Number(e.target.value) / 100 })} style={{ width: '100%' }} />
                        </div>
                      </div>
                    )}

                    <Row label="Alignment">
                      <div className="d-flex gap-1">
                        {['left', 'center', 'right'].map(a => (
                          <button key={a} onClick={() => patchOverlay(selected.id, { align: a })} style={segBtn(selected.align === a)}>
                            {a === 'left' ? <Icon.AlignLeft size={14} /> : a === 'center' ? <Icon.AlignCenter size={14} /> : <Icon.AlignRight size={14} />}
                          </button>
                        ))}
                      </div>
                    </Row>

                    <Row label="Timing">
                      <label className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', color: '#475569' }}>
                        <input type="checkbox" checked={selected.fullDuration} onChange={e => patchOverlay(selected.id, { fullDuration: e.target.checked })} />
                        Show for the whole video
                      </label>
                    </Row>
                    {!selected.fullDuration && (
                      <div className="d-flex gap-2 mb-1">
                        <div className="flex-fill">
                          <label style={smallLabel}>Start (s)</label>
                          <input type="number" min={0} step={0.5} value={selected.start} onChange={e => patchOverlay(selected.id, { start: e.target.value })} style={field} />
                        </div>
                        <div className="flex-fill">
                          <label style={smallLabel}>End (s){duration ? ` · max ${duration.toFixed(1)}` : ''}</label>
                          <input type="number" min={0} step={0.5} value={selected.end} onChange={e => patchOverlay(selected.id, { end: e.target.value })} style={field} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Export overlay (batch) ── */}
        {(exporting || exportJobs.length > 0) && (
          <div style={modalWrap} onClick={() => { if (!exporting) { setExportJobs([]); setResults([]); setExportError(''); } }}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
              {exporting ? (
                <div>
                  <div className="text-center mb-3">
                    <Icon.Loader size={28} className="sp-spin" style={{ color: '#4f46e5', marginBottom: 8 }} />
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      Rendering {exportJobs.length > 1 ? `${exportJobs.length} videos` : 'your video'}…
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.84rem', margin: '4px 0 0' }}>Burning in your text and exporting MP4s. This can take a minute or two each.</p>
                  </div>
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {exportJobs.map((j, i) => (
                      <div key={j.sourceId || i} className="d-flex align-items-center gap-2" style={{ padding: '7px 2px', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                        {j.status === 'done' ? <Icon.CheckCircle size={16} color="#16a34a" />
                          : j.status === 'failed' ? <Icon.XCircle size={16} color="#ef4444" />
                          : j.status === 'rendering' ? <Icon.Loader size={16} className="sp-spin" color="#f59e0b" />
                          : <Icon.Clock size={16} color="#94a3b8" />}
                        <span style={{ fontSize: '0.85rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{j.title}</span>
                        <span style={{ fontSize: '0.74rem', fontWeight: j.status === 'rendering' ? 600 : 400, color: j.status === 'failed' ? '#ef4444' : j.status === 'rendering' ? '#b45309' : '#94a3b8' }}>
                          {j.status === 'done' ? 'Done' : j.status === 'failed' ? 'Failed' : j.status === 'rendering' ? 'Rendering…' : 'Queued'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-center">
                    <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                      <Icon.Check size={24} color="#16a34a" />
                    </div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
                      {results.length > 1 ? `${results.length} videos exported` : 'Saved to your library'}
                    </h3>
                  </div>

                  {results.length === 1 ? (
                    <video src={results[0].file_url} controls style={{ width: '100%', borderRadius: 10, marginTop: 12, maxHeight: 320, background: '#000' }} />
                  ) : (
                    <div className="d-flex gap-2 flex-wrap justify-content-center mt-3">
                      {results.map(v => (
                        <video key={v.id} src={`${v.file_url}#t=0.1`} preload="metadata" style={{ width: 92, height: 124, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                      ))}
                    </div>
                  )}

                  {exportJobs.some(j => j.status === 'failed') && (
                    <div className="alert alert-warning py-2 mt-3" style={{ fontSize: '0.82rem' }}>
                      {exportJobs.filter(j => j.status === 'failed').length} video(s) failed to render. The rest were saved.
                    </div>
                  )}

                  <div className="d-flex gap-2 justify-content-center mt-3 flex-wrap">
                    {results.length > 0 && (
                      <button onClick={openScheduler} style={primaryBtn(false)}>
                        <Icon.Send size={15} /> {results.length > 1 ? `Schedule ${results.length} videos` : 'Schedule this video'}
                      </button>
                    )}
                    <button onClick={() => navigate('/media-library')} style={ghostBtn}>Open media library</button>
                    <button onClick={() => { setExportJobs([]); setResults([]); }} style={ghostBtn}>Keep editing</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Schedule modal ── */}
        {showSchedule && (
          <div style={modalWrap} onClick={() => { if (!scheduling) setShowSchedule(false); }}>
            <div style={{ ...modalCard, maxWidth: 540, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Schedule {results.length > 1 ? `${results.length} videos` : 'video'}
                </h3>
                <button onClick={() => setShowSchedule(false)} style={ghostBtn}><Icon.X size={15} /></button>
              </div>

              {scheduleDone ? (
                <div className="text-center py-2">
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <Icon.Check size={24} color="#16a34a" />
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Scheduled {scheduleDone.ok} post{scheduleDone.ok === 1 ? '' : 's'}</h4>
                  {scheduleDone.fail > 0 && <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{scheduleDone.fail} could not be scheduled.</p>}
                  <p style={{ color: '#64748b', fontSize: '0.85rem' }}>To {selectedAccounts.length} account{selectedAccounts.length === 1 ? '' : 's'}.</p>
                  <div className="d-flex gap-2 justify-content-center mt-3">
                    <button onClick={() => navigate('/schedule-posts')} style={primaryBtn(false)}>View scheduled posts</button>
                    <button onClick={() => { setShowSchedule(false); }} style={ghostBtn}>Done</button>
                  </div>
                </div>
              ) : (
                <>
                  {scheduleError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{scheduleError}</div>}

                  <label style={smallLabel}>Caption (optional)</label>
                  <textarea value={postText} onChange={e => setPostText(e.target.value)} rows={2} style={field} placeholder="Write a caption for these posts…" />

                  <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 12 }}>
                    <label style={{ ...smallLabel, margin: 0 }}>Post to {results.length > 1 ? '(same accounts for all videos)' : ''}</label>
                    {selectedAccounts.length > 0 && (
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {selectedAccounts.length} selected · <span style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => setSelectedAccounts([])}>Clear</span>
                      </span>
                    )}
                  </div>
                  {accountsLoading ? (
                    <div className="text-center py-3"><Icon.Loader size={20} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
                  ) : accounts.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No connected accounts. <span style={{ color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/social-accounts')}>Connect an account</span></div>
                  ) : (
                    <>
                      {/* Platform filter */}
                      <div className="d-flex gap-1 flex-wrap mt-1 mb-2">
                        <button onClick={() => setAccountTypeFilter('')} style={filterChip(accountTypeFilter === '')}>All ({accounts.length})</button>
                        {accountTypes.map(t => (
                          <button key={t} onClick={() => setAccountTypeFilter(t)} style={filterChip(accountTypeFilter === t)}>
                            {t} ({accounts.filter(a => a.type === t).length})
                          </button>
                        ))}
                      </div>
                      {/* Name search */}
                      <input value={accountSearch} onChange={e => setAccountSearch(e.target.value)} placeholder="Search accounts…" style={{ ...field, marginBottom: 8 }} />
                      {/* Scrollable list */}
                      <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 8, padding: 8 }}>
                        {filteredAccounts.length === 0 ? (
                          <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '8px 4px' }}>No accounts match.</div>
                        ) : filteredAccounts.map(acc => {
                          const key = `${acc.id}_${acc.type}`;
                          const on = selectedAccounts.includes(key);
                          return (
                            <button key={key} onClick={() => toggleAccount(acc)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px 5px', borderRadius: 20, cursor: 'pointer', fontSize: '0.8rem', border: on ? '1px solid #4f46e5' : '1px solid #e2e8f0', background: on ? '#eef2ff' : '#fff', color: on ? '#4f46e5' : '#475569' }}>
                              {acc.image
                                ? <img src={acc.image} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                                : <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#cbd5e1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#fff' }}>{(acc.name || '?').charAt(0).toUpperCase()}</span>}
                              <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name || acc.type}</span>
                              <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{acc.type}</span>
                              {on && <Icon.Check size={13} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <label style={{ ...smallLabel, marginTop: 12 }}>{results.length > 1 ? 'Start date & time (first video)' : 'Date & time'}</label>
                  <input type="datetime-local" value={scheduleStart} onChange={e => setScheduleStart(e.target.value)} style={field} />

                  {results.length > 1 && (
                    <>
                      <label style={{ ...smallLabel, marginTop: 12 }}>Space the videos</label>
                      <div className="d-flex flex-wrap gap-1">
                        {SPACING_PRESETS.map(p => (
                          <button key={p.hours} onClick={() => setSpacingHours(p.hours)} style={segBtn(spacingHours === p.hours)}>{p.label}</button>
                        ))}
                      </div>
                      <div style={{ marginTop: 10, maxHeight: 150, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 8, padding: '6px 10px' }}>
                        {results.map((v, i) => (
                          <div key={v.id} className="d-flex align-items-center justify-content-between" style={{ fontSize: '0.78rem', color: '#475569', padding: '2px 0' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Video {i + 1}</span>
                            <span style={{ color: '#0f172a', fontWeight: 600 }}>{scheduleDateFor(i).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="d-flex gap-2 justify-content-end mt-3">
                    <button onClick={() => setShowSchedule(false)} style={ghostBtn}>Cancel</button>
                    <button onClick={doSchedule} disabled={scheduling || !selectedAccounts.length} style={primaryBtn(scheduling || !selectedAccounts.length)}>
                      {scheduling ? 'Scheduling…' : `Schedule ${results.length > 1 ? `${results.length} posts` : 'post'}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Image sticker picker (reuses the media library picker) */}
        <MediaPicker show={showSticker} onHide={() => setShowSticker(false)} type="images" onSelect={addSticker} />

        {/* ── Audio (music) picker modal ── */}
        {showAudio && (
          <div style={modalWrap} onClick={() => setShowAudio(false)}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Add music</h3>
                <button onClick={() => setShowAudio(false)} style={ghostBtn}><Icon.X size={15} /></button>
              </div>

              <div className="d-flex gap-2 mb-3">
                {['upload', 'library'].map(t => (
                  <button key={t} onClick={() => setAudioTab(t)} style={tabBtn(audioTab === t)}>
                    {t === 'upload' ? 'Upload MP3' : 'My audio library'}
                  </button>
                ))}
              </div>

              {audioError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{audioError}</div>}

              {audioTab === 'upload' ? (
                <label style={{ ...dropzone, minHeight: 150 }}>
                  <input type="file" accept="audio/*,.mp3" hidden disabled={audioUploading}
                    onChange={e => handleAudioUpload(e.target.files?.[0])} />
                  {audioUploading ? (
                    <div className="text-center"><Icon.Loader size={24} className="sp-spin" style={{ color: '#4f46e5' }} /><div style={{ fontSize: '0.85rem', color: '#334155', marginTop: 8 }}>Uploading…</div></div>
                  ) : (
                    <div className="text-center">
                      <Icon.Music size={30} style={{ color: '#94a3b8', marginBottom: 8 }} />
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>Click to upload an MP3</div>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 }}>It will replace the video's audio</div>
                    </div>
                  )}
                </label>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {audioLibLoading ? (
                    <div className="text-center py-4"><Icon.Loader size={22} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
                  ) : audioLib.length === 0 ? (
                    <div className="text-center py-4" style={{ color: '#94a3b8', fontSize: '0.88rem' }}>No audio uploaded yet.</div>
                  ) : (
                    audioLib.map(a => (
                      <div key={a.id} style={layerRow} onClick={() => { setAudio({ id: a.id, file_url: a.file_url, title: a.title, volume: 1 }); setShowAudio(false); }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Icon.Music size={14} color="#4f46e5" /> {a.title || 'Audio track'}
                        </span>
                        <Icon.Plus size={15} style={{ color: '#4f46e5' }} />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainStructure>
  );
}

// ── small presentational helpers ──
function Row({ label, children }) {
  return (
    <div className="mb-2">
      <label style={smallLabel}>{label}</label>
      {children}
    </div>
  );
}

// Inline-editable project name — saves on blur / Enter, like the media library.
function ProjectName({ project, onSave }) {
  const [name, setName] = useState(project.name || '');
  useEffect(() => { setName(project.name || ''); }, [project.name]);
  const commit = () => { const n = name.trim(); if (n && n !== project.name) onSave(project.id, n); };
  return (
    <input
      value={name}
      onChange={e => setName(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      onClick={e => e.stopPropagation()}
      title="Click to rename"
      style={{ width: '100%', border: '1px solid transparent', borderRadius: 6, padding: '2px 4px', fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', background: 'transparent', outline: 'none' }}
      onFocus={e => { e.target.style.border = '1px solid #c7d2fe'; e.target.style.background = '#fff'; }}
      onBlurCapture={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
    />
  );
}
function fmtDate(s) {
  if (!s) return '';
  try { return new Date(String(s).replace(' ', 'T')).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

// Serialize one editor overlay into the render-video payload shape.
function serializeOverlay(o) {
  return o.type === 'image'
    ? {
        type: 'image', source_image_id: o.source_image_id,
        width: o.width, opacity: o.opacity,
        x: o.x, y: o.y,
        start: o.fullDuration ? 0 : Number(o.start) || 0,
        end: o.fullDuration ? null : (Number(o.end) || null),
      }
    : {
        type: 'text', text: o.text, font: o.font, fontSize: o.fontSize,
        color: o.color,
        bgColor: o.bgEnabled ? o.bgColor : null,
        bgOpacity: o.bgOpacity, radius: o.radius, padding: o.padding,
        outline: !!o.outline, outlineColor: o.outlineColor, outlineWidth: o.outlineWidth,
        wrap: o.wrap !== false, wrapWidth: o.wrapWidth,
        x: o.x, y: o.y, align: o.align,
        start: o.fullDuration ? 0 : Number(o.start) || 0,
        end: o.fullDuration ? null : (Number(o.end) || null),
      };
}
function hexToRgba(hex, a) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(0,0,0,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// ── styles ──
const card = { background: '#fff', border: '1px solid #e8edf2', borderRadius: 14, padding: 20 };
const ghostBtn = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: '#475569', fontSize: '0.82rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 };
const primaryBtn = (disabled) => ({ background: disabled ? '#c7d2fe' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontWeight: 600, fontSize: '0.85rem', cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 });
const tabBtn = (active) => ({ background: active ? '#eef2ff' : '#fff', color: active ? '#4f46e5' : '#64748b', border: `1px solid ${active ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' });
const filterChip = (active) => ({ background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#64748b', border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`, borderRadius: 16, padding: '3px 10px', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', textTransform: 'capitalize' });
const dropzone = { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, border: '2px dashed #cbd5e1', borderRadius: 12, cursor: 'pointer', background: '#f8fafc', transition: 'border-color .15s, background .15s' };
const dropzoneActive = { ...dropzone, border: '2px dashed #4f46e5', background: '#eef2ff' };
const libItem = { width: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 6, cursor: 'pointer', textAlign: 'left' };
const panelLabel = { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 10 };
const smallLabel = { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: 4 };
const field = { width: '100%', border: '1px solid #d1d9e0', borderRadius: 8, padding: '8px 10px', fontSize: '0.85rem', color: '#0f172a', outline: 'none', boxSizing: 'border-box' };
const colorInput = { width: '100%', height: 34, border: '1px solid #d1d9e0', borderRadius: 8, padding: 2, cursor: 'pointer', background: '#fff' };
const layerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#334155' };
const miniBtn = { background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', color: '#475569' };
const segBtn = (active) => ({ flex: 1, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#64748b', border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`, borderRadius: 7, padding: '6px 0', cursor: 'pointer', display: 'flex', justifyContent: 'center' });
const modalWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 16, padding: 28, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };

export default VideoEditor;
